/**
 * Utility functions for ITAR qualified classifications
 */

import { QuestionModel } from '../models/question-model';
import { Item } from '../models/item-model';

/**
 * ITAR code descriptions for user-friendly display
 */
export const ITAR_CODE_DESCRIPTIONS: Record<string, string> = {
  'XI(a)(7)': 'DoD Development Funding',
  'XI(a)(5)(ii)': 'IFF Modes 4/5',
  'XI(a)(5)(iii)': 'ECCM Implementation',
  'XI(a)(5)(iv)': 'TEMPEST Compliance',
  'XI(a)(5)(v)': 'Signal Transmission',
  'XI(b)': 'Intelligence Purposes',
  'XI(c)(1)': 'ASICs/PLDs',
  'XI(c)(2)': 'PCBs',
  'XI(c)(3)': 'Multichip Modules',
  'XI(c)(4)': 'T/R Modules',
  'XI(c)(18)': 'Info Security Components',
  'XIII(b)': 'Cryptographic Systems'
};

/**
 * Checks if a user's answer matches an option's criteria, respecting the operator
 */
function matchesCriteria(
  answer: string | string[],
  criteria: { Value: string; Operator?: string }
): boolean {
  const value = criteria.Value;
  const operator = (criteria.Operator || 'equals').toLowerCase();

  switch (operator) {
    case 'not':
      return Array.isArray(answer)
        ? !answer.includes(value)
        : answer !== value;
    case 'contains':
      return Array.isArray(answer)
        ? answer.includes(value)
        : (typeof answer === 'string' && answer.includes(value));
    case 'text':
      return Array.isArray(answer)
        ? answer.some(a => a.trim().length > 0)
        : (typeof answer === 'string' && answer.trim().length > 0);
    case 'equals':
    default:
      return Array.isArray(answer)
        ? answer.includes(value)
        : value === answer;
  }
}

/**
 * Derives the list of ITAR classifications an item qualifies for
 * based on the user's questionnaire answers
 * 
 * @param questionnaire - The questionnaire definition containing questions and options
 * @param itemAnswers - The user's answers to the questionnaire
 * @returns Array of ITAR codes the item qualifies for
 */
export function deriveQualifiedITARClassifications(
  questionnaire: QuestionModel.Data[],
  itemAnswers: Item.Data["ITARQuestionnaireAnswers"]
): string[] {
  if (!questionnaire || !itemAnswers || itemAnswers.length === 0) {
    return [];
  }

  const qualifiedCodes: string[] = [];

  // Iterate through all user answers
  for (const userAnswer of itemAnswers) {
    // Find the corresponding question in the questionnaire
    const question = questionnaire.find(q => q.Id === userAnswer.question);
    if (!question) continue;

    // Check each option in the question
    for (const option of question.Options) {
      // Check if this option matches the user's answer, respecting the operator
      const answerMatches = matchesCriteria(userAnswer.answer, option.Criteria);

      // If the option matches the user's answer AND has QualifiesFor
      if (answerMatches && option.QualifiesFor) {
        // Only add if not already in the list (avoid duplicates)
        if (!qualifiedCodes.includes(option.QualifiesFor)) {
          qualifiedCodes.push(option.QualifiesFor);
        }
      }
    }
  }

  return qualifiedCodes;
}

/**
 * Gets a user-friendly description for an ITAR code
 * 
 * @param code - ITAR code (e.g., "XI(a)(7)")
 * @returns Friendly description or the code itself if no description exists
 */
export function getITARCodeDescription(code: string): string {
  return ITAR_CODE_DESCRIPTIONS[code] || code;
}

/**
 * Formats qualified ITAR codes for display
 * 
 * @param codes - Array of ITAR codes
 * @returns Array of formatted strings with code and description
 */
export function formatQualifiedITARCodes(codes: string[]): string[] {
  return codes.map(code => {
    const description = ITAR_CODE_DESCRIPTIONS[code];
    return description ? `${code} - ${description}` : code;
  });
}
